import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";

import { customerPaymentMethodsSchema } from "~/shared/schemas/invoice-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";

const fetchCustomerAccounts = async (
	signal: AbortSignal,
	customerId: number,
) => {
	try {
		const resp = await getAndValidate(
			apiRoutes.GETCUSTOMERACCOUNTS,
			customerPaymentMethodsSchema,
			{
				overrideHook: (request) => {
					request.headers.set(
						"Tql-Identity-Companyid",
						String(customerId ?? ""),
					);
					request.headers.set("Tql-Identity-Accounttype", String(2));
				},
				signal: signal,
			},
		);
		if (resp.success) {
			return {
				...resp.data,
				paymentAccounts: resp.data.paymentAccounts.map((account) => ({
					...account,
					Last4:
						account.paymentMethod === "CreditCard"
							? account.cardNumberLast4
							: account.accountNumberLast4,
				})),
			};
		} else {
			console.error("Failed to fetch customer accounts:", resp.error);
			return null;
		}
	} catch (error) {
		console.error("Failed to fetch Braintree token:", error);
		return null;
	}
};

export const useGetCustomerAccounts = ({
	enabled = true,
}: { enabled?: boolean } = {}) => {
	const [selectedCustomer] = useAtom(selectedCustomerAtom);
	const { data, isLoading, error } = useQuery({
		queryKey: ["customerAccounts", selectedCustomer?.id ?? 0],
		queryFn: async ({ signal }) => {
			return await fetchCustomerAccounts(signal, selectedCustomer?.id ?? 0);
		},
		enabled,
		staleTime: 30_000,
		gcTime: 5 * 60_000,
	});
	return {
		accounts: data,
		isLoading,
		error,
	};
};
